import React, { useState, useTransition, useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { IconButton } from '@spotoninc/nexus-react';

import { ThumbsUpIcon, ThumbsUpSolidIcon } from '@spotoninc/nexus-web-assets';

import styles from './feedbackBlock.module.css';

export const FEEDBACK_STATE = {
  NONE: 0,
  POSITIVE: 1,
  NEGATIVE: -1,
};

function FeedbackBlock({
  label,
  align,
  iconAlign,
  onThumbsUpClick,
  onThumbsDownClick,
  value,
}) {
  const [feedbackState, setFeedbackState] = useState(value);
  const [isPending, startTransition] = useTransition();

  // Sync optimistic value with actual value when it changes
  useEffect(() => {
    setFeedbackState(value);
  }, [value]);

  const handleThumbsUpClick = e => {
    e?.preventDefault();

    // Immediately update feedback state
    setFeedbackState(FEEDBACK_STATE.POSITIVE);

    // Start transition for the actual update
    startTransition(() => {
      try {
        const result = onThumbsUpClick?.(e);

        // Handle promise if returned
        if (result && typeof result.catch === 'function') {
          result.catch(error => {
            console.error('Error submitting positive feedback:', error);
            // Revert optimistic state on error
            setFeedbackState(value);
          });
        }
      } catch (error) {
        console.error('Error submitting positive feedback:', error);
        // Revert optimistic state on error
        setFeedbackState(value);
      }
    });
  };

  const handleThumbsDownClick = e => {
    e?.preventDefault();

    // Immediately update optimistic state
    setFeedbackState(FEEDBACK_STATE.NEGATIVE);

    // Start transition for the actual update
    startTransition(() => {
      try {
        const result = onThumbsDownClick?.(e);

        // Handle promise if returned
        if (result && typeof result.catch === 'function') {
          result.catch(error => {
            console.error('Error submitting negative feedback:', error);
            // Revert optimistic state on error
            setFeedbackState(value);
          });
        }
      } catch (error) {
        console.error('Error submitting negative feedback:', error);
        // Revert optimistic state on error
        setFeedbackState(value);
      }
    });
  };

  return (
    <Flex direction={'column'} gap={2} className={styles.container}>
      <Box textAlign={align || 'center'} className={styles.text}>
        {label || 'Help us get better! Are these insights helpful for you?'}
      </Box>
      <Flex direction={'row'} justifyContent={iconAlign || 'center'} gap={2}>
        <IconButton
          icon={
            feedbackState === FEEDBACK_STATE.POSITIVE
              ? ThumbsUpSolidIcon
              : ThumbsUpIcon
          }
          className={[
            styles.iconButton,
            feedbackState === FEEDBACK_STATE.POSITIVE ? styles.active : '',
          ].join(' ')}
          size="small"
          onClick={handleThumbsUpClick}
          disabled={isPending}
        />
        <IconButton
          icon={
            feedbackState === FEEDBACK_STATE.NEGATIVE
              ? ThumbsUpSolidIcon
              : ThumbsUpIcon
          }
          className={[
            styles.iconButton,
            feedbackState === FEEDBACK_STATE.NEGATIVE ? styles.active : '',
          ].join(' ')}
          size="small"
          style={{ rotate: '180deg', transform: 'scaleX(-1)' }}
          onClick={handleThumbsDownClick}
          disabled={isPending}
        />
      </Flex>
    </Flex>
  );
}

export default FeedbackBlock;
